// src/modules/smelter/pages/SmelterDirectory.jsx
import React, { useEffect, useState } from 'react';
import { fetchSmelters, createSmelter, updateSmelter, deleteSmelter } from '../api';
import {
  Table, Button, Typography, Spin, Tag, Row, Col, Input,
  Modal, Form, Switch, message, Popconfirm, Space,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';

const { Title } = Typography;

export default function SmelterDirectory() {
  const navigate = useNavigate();
  const { canViewCreateModule, canEditModule, can, canViewField, canEditField } = usePermissions();

  // List-level field visibility
  const canViewName = canViewField('smelter', 'name');
  const canViewContact = canViewField('smelter', 'contactPerson');
  const canViewPhone = canViewField('smelter', 'phone');
  const canViewGstin = canViewField('smelter', 'gstin');
  const canViewPan = canViewField('smelter', 'pan');
  const canViewState = canViewField('smelter', 'state');
  const canViewIsActive = canViewField('smelter', 'isActive');

  // Form-level two-tier permissions
  const canFieldNameShow = canEditField('smelter', 'name') || canViewField('smelter', 'name');
  const canFieldNameEdit = canEditField('smelter', 'name');
  const canFieldContactShow = canEditField('smelter', 'contactPerson') || canViewField('smelter', 'contactPerson');
  const canFieldContactEdit = canEditField('smelter', 'contactPerson');
  const canFieldPhoneShow = canEditField('smelter', 'phone') || canViewField('smelter', 'phone');
  const canFieldPhoneEdit = canEditField('smelter', 'phone');
  const canFieldEmailShow = canEditField('smelter', 'email') || canViewField('smelter', 'email');
  const canFieldEmailEdit = canEditField('smelter', 'email');
  const canFieldAddressShow = canEditField('smelter', 'address') || canViewField('smelter', 'address');
  const canFieldAddressEdit = canEditField('smelter', 'address');
  const canFieldGstinShow = canEditField('smelter', 'gstin') || canViewField('smelter', 'gstin');
  const canFieldGstinEdit = canEditField('smelter', 'gstin');
  const canFieldPanShow = canEditField('smelter', 'pan') || canViewField('smelter', 'pan');
  const canFieldPanEdit = canEditField('smelter', 'pan');
  const canFieldStateShow = canEditField('smelter', 'state') || canViewField('smelter', 'state');
  const canFieldStateEdit = canEditField('smelter', 'state');
  const canFieldIsActiveShow = canEditField('smelter', 'isActive') || canViewField('smelter', 'isActive');
  const canFieldIsActiveEdit = canEditField('smelter', 'isActive');
  const [smelters, setSmelters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => { load(); }, [search]);

  const load = async () => {
    setLoading(true);
    try { const res = await fetchSmelters({ search }); setSmelters(res.data || []); }
    catch { messageApi.error('Failed to load smelters'); }
    finally { setLoading(false); }
  };

  const openModal = (smelter = null) => {
    setEditing(smelter);
    form.setFieldsValue(smelter || { isActive: true });
    setModalOpen(true);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      if (editing) { await updateSmelter(editing._id, values); messageApi.success('Smelter updated'); }
      else { await createSmelter(values); messageApi.success('Smelter created'); }
      setModalOpen(false); form.resetFields(); load();
    } catch (e) { messageApi.error(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await deleteSmelter(id); messageApi.success('Smelter deleted'); load(); }
    catch (e) { messageApi.error(e.response?.data?.message || 'Delete failed'); }
  };

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name', width: 200 },
    canViewContact && { title: 'Contact', dataIndex: 'contactPerson', key: 'contactPerson', width: 150 },
    canViewPhone && { title: 'Phone', dataIndex: 'phone', key: 'phone', width: 130 },
    canViewGstin && { title: 'GSTIN', dataIndex: 'gstin', key: 'gstin', width: 160 },
    canViewPan && { title: 'PAN', dataIndex: 'pan', key: 'pan', width: 140 },
    canViewState && { title: 'State', dataIndex: 'state', key: 'state', width: 130 },
    canViewIsActive && { title: 'Status', key: 'isActive', dataIndex: 'isActive', width: 90,
      render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Tag> },
    { title: 'Actions', key: 'actions', width: 130,
      render: (_, r) => (
        <Space>
          {canEditModule('smelter') && (
            <Button size="small" icon={<EditOutlined />} onClick={() => openModal(r)}>Edit</Button>
          )}
          {can('smelter', 'delete') && (
            <Popconfirm title="Delete this smelter?" onConfirm={() => handleDelete(r._id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ) },
  ].filter(Boolean);

  return (
    <div style={{ padding: 24 }}>
      {contextHolder}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={4} style={{ margin: 0 }}>Smelter Directory</Title></Col>
        <Col>
          <Space>
            <Button onClick={() => navigate('/smelters/dispatches')}>View Dispatches</Button>
            {canViewCreateModule('smelter') && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>Add Smelter</Button>
            )}
          </Space>
        </Col>
      </Row>

      <Row style={{ marginBottom: 12 }}>
        <Col xs={24} sm={12}>
          <Input.Search
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
          />
        </Col>
      </Row>

      <Spin spinning={loading}>
        <Table dataSource={smelters} rowKey="_id" columns={columns} pagination={false} scroll={{ x: 700 }} size="small" />
      </Spin>

      <Modal
        title={editing ? 'Edit Smelter' : 'Add Smelter'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        onOk={() => form.submit()}
        confirmLoading={saving}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          {canFieldNameShow && (
            <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Required' }]}>
              <Input disabled={!canFieldNameEdit} />
            </Form.Item>
          )}
          {canFieldContactShow && (
            <Form.Item label="Contact Person" name="contactPerson"><Input disabled={!canFieldContactEdit} /></Form.Item>
          )}
          {canFieldPhoneShow && (
            <Form.Item label="Phone" name="phone"><Input disabled={!canFieldPhoneEdit} /></Form.Item>
          )}
          {canFieldEmailShow && (
            <Form.Item label="Email" name="email"><Input disabled={!canFieldEmailEdit} /></Form.Item>
          )}
          {canFieldAddressShow && (
            <Form.Item label="Address" name="address"><Input.TextArea rows={2} disabled={!canFieldAddressEdit} /></Form.Item>
          )}
          {canFieldGstinShow && (
            <Form.Item label="GSTIN" name="gstin"><Input disabled={!canFieldGstinEdit} /></Form.Item>
          )}
          {canFieldPanShow && (
            <Form.Item label="PAN" name="pan"><Input disabled={!canFieldPanEdit} /></Form.Item>
          )}
          {canFieldStateShow && (
            <Form.Item label="State" name="state"><Input disabled={!canFieldStateEdit} /></Form.Item>
          )}
          <Form.Item label="Notes" name="notes"><Input.TextArea rows={2} /></Form.Item>
          {canFieldIsActiveShow && (
            <Form.Item label="Active" name="isActive" valuePropName="checked">
              <Switch disabled={!canFieldIsActiveEdit} />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}
